'use client'

import { E_EARN_VAULT_CHAIN_ID } from '@/config/euler-contracts'
import { getMarketVaultSortRank } from '@/config/market-config'
import { paths } from '@/config/paths'
import { USD_MOCK_TOKEN } from '@/domain/types/Token'
import { HYPEREVM_PRODUCTS } from '@/features/evault-details/data/products'
import { Skeleton } from '@/ui/atoms/skeleton/Skeleton'
import { PageLayout } from '@/ui/layouts/PageLayout'
import { PageHero } from '@/ui/molecules/page-hero/PageHero'
import { PillNav } from '@/ui/molecules/page-hero/PillNav'
import { withSuspense } from '@/ui/utils/withSuspense'
import { Boxes, Rows3, Shield } from 'lucide-react'
import { useRouter } from 'next/navigation'
import { useMemo } from 'react'
import { useAccount } from 'wagmi'
import { EVaultListView } from './components/EVaultListView'
import { EarnFeatureCards } from './components/EarnFeatureCards'
import { EarnVaultShowcase } from './components/EarnVaultShowcase'
import { VaultsContainerSkeleton } from './components/skeleton/VaultsContainerSkeleton'
import { useEVaults } from './logic/useEVaults'
import { useEarnStats } from './logic/useEarnStats'

function VaultsContainer() {
  const { address, isConnecting } = useAccount()
  const { vaults, isLoading, error } = useEVaults(address)
  const router = useRouter()

  // Filter to only HypurrFi Earn vaults
  const allowedVaults = useMemo(() => {
    const product = HYPEREVM_PRODUCTS.get('hypurrfi-earn')
    if (!product) return new Set<string>()
    return new Set(product.vaults.map((addr) => addr.toLowerCase()))
  }, [])

  const filteredVaults = useMemo(() => {
    return vaults.filter((vault) => allowedVaults.has(vault.address.toLowerCase()))
  }, [vaults, allowedVaults])

  const featuredVaults = useMemo(
    () => [...filteredVaults].sort((a, b) => (b.supplyAPY ?? 0) - (a.supplyAPY ?? 0)),
    [filteredVaults],
  )

  const tableVaults = useMemo(
    () =>
      [...featuredVaults].sort((a, b) => {
        const aRank = getMarketVaultSortRank('earn', [a.address, a.underlyingAsset])
        const bRank = getMarketVaultSortRank('earn', [b.address, b.underlyingAsset])
        return aRank - bRank
      }),
    [featuredVaults],
  )

  const earnStats = useEarnStats(filteredVaults)

  const handleVaultClick = (vaultAddress: string) => {
    router.push(
      paths.lendDetails.replace(':chainId', E_EARN_VAULT_CHAIN_ID.toString()).replace(':vaultAddress', vaultAddress),
    )
  }

  const showLoadingState = isConnecting || isLoading

  return (
    <PageLayout className="relative max-w-6xl gap-6 px-3 sm:gap-8">
      {/* Hero */}
      <div className="relative">
        <PageHero
          title="Earn"
          subtitle="Curated DeFi strategies for passive yield generation. Deposit into professionally managed vaults and earn optimized returns."
          journey={['Choose vault', 'Deposit', '💰 Earn yield']}
          pillNav={
            <PillNav
              items={[
                {
                  label: 'Markets',
                  href: paths.earn,
                  icon: <Boxes className="h-4 w-4" />,
                },
                {
                  label: 'Assets',
                  href: paths.earnAssets,
                  icon: <Rows3 className="h-4 w-4" />,
                },
                {
                  label: 'Vaults',
                  href: paths.earnVaults,
                  icon: <Shield className="h-4 w-4" />,
                  active: true,
                },
              ]}
            />
          }
          stats={[
            { label: 'Total TVL', value: USD_MOCK_TOKEN.formatUSD(earnStats.totalTVL, { compact: true }) },
            { label: 'Active Vaults', value: String(filteredVaults.length) },
            { label: 'Curator', value: 'Clearstar', icon: <Shield className="h-3 w-3 text-muted-foreground" /> },
          ]}
          statsLoading={showLoadingState}
        />
      </div>

      {/* Featured Vault Cards */}
      <div className="relative">
        {showLoadingState ? (
          <FeaturedVaultsSkeleton />
        ) : (
          <EarnVaultShowcase vaults={featuredVaults} onVaultClick={handleVaultClick} />
        )}
      </div>

      {/* Vault List Table */}
      <div className="relative">
        {showLoadingState ? (
          <ListSkeleton />
        ) : error ? (
          <div className="flex flex-col items-center justify-center gap-2 py-16">
            <p className="text-lg text-white/60">Error loading vaults</p>
            <p className="text-sm text-white/40">{error.message}</p>
          </div>
        ) : tableVaults.length > 0 ? (
          <div className="flex flex-col gap-3">
            <span className="px-1 font-semibold text-foreground text-sm">All Vaults</span>
            <EVaultListView vaults={tableVaults} />
          </div>
        ) : (
          <div className="flex flex-col items-center justify-center gap-2 py-16">
            <p className="text-lg text-white/60">No vaults available</p>
            <p className="text-sm text-white/40">Check back soon for new earn opportunities</p>
          </div>
        )}
      </div>

      {/* Feature Cards + How it Works */}
      <div className="relative">
        <EarnFeatureCards />
      </div>
    </PageLayout>
  )
}

function FeaturedVaultsSkeleton() {
  return (
    <div className="flex flex-col gap-3">
      <Skeleton className="h-5 w-40" />
      <div className="grid grid-cols-2 gap-3 md:grid-cols-4">
        {[1, 2, 3, 4].map((i) => (
          <Skeleton key={i} className="h-[170px] rounded-xl" />
        ))}
      </div>
    </div>
  )
}

function ListSkeleton() {
  return (
    <div className="flex flex-col gap-3">
      <Skeleton className="h-5 w-24" />
      <div className="flex flex-col gap-2">
        {[1, 2, 3, 4].map((i) => (
          <Skeleton key={i} className="h-14 w-full rounded-lg" />
        ))}
      </div>
    </div>
  )
}

export const VaultsContainerWithSuspense = withSuspense(VaultsContainer, VaultsContainerSkeleton)
